import * as React from 'react'
import { format } from 'date-fns'
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react'
import type { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

/**
 * Figma (node 334:4051 — Date Picker, node 18415:7484 — Time Picker):
 *
 * Trigger: h-8 (32px), rounded-lg (10px), px-2.5 (10px), py-2 (8px),
 *   gap-2, border border-input, bg-background, text-sm (14px)
 *
 * Trigger Types:
 *   icon-left  (node 334:4050):  CalendarIcon left + text
 *   with-input (node 18403:6198): text + CalendarIcon button right
 *   icon-right (node 18403:7912): text + ChevronDown right
 *
 * States:
 *   Default:  border-input, bg-background
 *   Hover:    bg-accent
 *   Focus:    border-ring, ring-1 ring-ring/50
 *   Active:   bg-muted (with-input variant)
 *   Disabled: opacity-50, cursor-not-allowed
 *
 * Calendar (node 236:205):
 *   bg-background, border border-border, rounded-lg, p-2, gap-4
 *   Day cells: 28x28, rounded-lg (10px)
 *   Selected:  bg-primary, text-primary-foreground
 *   Today:     bg-muted
 *   Outside:   text-muted-foreground
 *   Headers:   text-xs, text-muted-foreground
 *
 * Time Picker (node 18415:7493):
 *   h-8, rounded-lg, px-2.5, py-1, border border-input, bg-background, w-[150px]
 *   text-sm, text-foreground
 *   Focus: border-ring, ring-1 ring-ring/50
 *   Disabled: opacity-50, cursor-not-allowed
 */

/* ─── Shared trigger styles ─── */

const triggerBase = cn(
  'inline-flex h-8 w-full items-center gap-2 rounded-lg border border-input',
  'bg-background px-2.5 py-2 text-sm text-foreground',
  'hover:bg-accent hover:text-accent-foreground',
  'focus-visible:outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/50',
  'disabled:cursor-not-allowed disabled:opacity-50',
)

export type DatePickerType = 'icon-left' | 'with-input' | 'icon-right'

/* ─── Trigger renderer ─── */

function TriggerContent({
  type,
  hasValue,
  label,
}: {
  type: DatePickerType
  hasValue: boolean
  label: string
}) {
  if (type === 'with-input') {
    return (
      <>
        <span className={cn('flex-1 truncate text-left', !hasValue && 'text-muted-foreground')}>
          {label}
        </span>
        <span className="flex size-6 shrink-0 items-center justify-center rounded-md">
          <CalendarIcon className="size-3.5" />
        </span>
      </>
    )
  }

  if (type === 'icon-right') {
    return (
      <>
        <span className={cn('flex-1 truncate text-left', !hasValue && 'text-muted-foreground')}>
          {label}
        </span>
        <ChevronDown className="size-4 shrink-0 opacity-50" />
      </>
    )
  }

  // icon-left (default)
  return (
    <>
      <CalendarIcon className="size-4 shrink-0" />
      <span className={cn('flex-1 truncate text-left', !hasValue && 'text-muted-foreground')}>
        {label}
      </span>
    </>
  )
}

/* ─── IntuiDatePicker (single date) ─── */

export interface IntuiDatePickerProps {
  /** Currently selected date */
  value?: Date
  /** Callback when a date is selected */
  onChange?: (date: Date | undefined) => void
  /** Placeholder text when no date is selected */
  placeholder?: string
  /** Date format string (date-fns) */
  dateFormat?: string
  /** Trigger type variant */
  type?: DatePickerType
  /** Disabled state */
  disabled?: boolean
  /** Additional className for the trigger */
  className?: string
  /** Calendar props forwarded to DayPicker */
  calendarProps?: Omit<React.ComponentProps<typeof Calendar>, 'mode' | 'selected' | 'onSelect'>
}

function IntuiDatePicker({
  value,
  onChange,
  placeholder,
  dateFormat = 'MMM dd, yyyy',
  type = 'icon-left',
  disabled = false,
  className,
  calendarProps,
}: IntuiDatePickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const resolvedPlaceholder = placeholder ?? t('common:date_picker.pick_a_date')
  const label = value && !isNaN(value.getTime()) ? format(value, dateFormat) : resolvedPlaceholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(triggerBase, className)}
        >
          <TriggerContent type={type} hasValue={!!value} label={label} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(date) => {
            onChange?.(date)
            setOpen(false)
          }}
          {...calendarProps}
        />
      </PopoverContent>
    </Popover>
  )
}
IntuiDatePicker.displayName = 'IntuiDatePicker'

/* ─── IntuiDateRangePicker ─── */

export interface IntuiDateRangePickerProps {
  /** Currently selected date range */
  value?: DateRange
  /** Callback when a range is selected */
  onChange?: (range: DateRange | undefined) => void
  /** Placeholder text */
  placeholder?: string
  /** Date format string (date-fns) */
  dateFormat?: string
  /** Trigger type variant */
  type?: DatePickerType
  /** Disabled state */
  disabled?: boolean
  /** Additional className for the trigger */
  className?: string
  /** Number of months to display */
  numberOfMonths?: number
  /** Calendar props forwarded to DayPicker */
  calendarProps?: Omit<
    React.ComponentProps<typeof Calendar>,
    'mode' | 'selected' | 'onSelect' | 'numberOfMonths'
  >
}

function IntuiDateRangePicker({
  value,
  onChange,
  placeholder,
  dateFormat = 'MMM dd, yyyy',
  type = 'icon-left',
  disabled = false,
  className,
  numberOfMonths = 2,
  calendarProps,
}: IntuiDateRangePickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const resolvedPlaceholder = placeholder ?? t('common:date_picker.pick_a_date_range')

  const label = React.useMemo(() => {
    if (!value?.from || isNaN(value.from.getTime())) return resolvedPlaceholder
    if (!value.to || isNaN(value.to.getTime())) return format(value.from, dateFormat)
    return `${format(value.from, dateFormat)} - ${format(value.to, dateFormat)}`
  }, [value, dateFormat, resolvedPlaceholder])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(triggerBase, className)}
        >
          <TriggerContent type={type} hasValue={!!value?.from} label={label} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={value}
          onSelect={(range) => {
            onChange?.(range)
            if (range?.from && range?.to) setOpen(false)
          }}
          numberOfMonths={numberOfMonths}
          {...calendarProps}
        />
      </PopoverContent>
    </Popover>
  )
}
IntuiDateRangePicker.displayName = 'IntuiDateRangePicker'

/* ─── IntuiTimePicker ─── */

export interface IntuiTimePickerProps {
  /** Current time value (HH:mm or HH:mm:ss) */
  value?: string
  /** Callback when time changes */
  onChange?: (time: string) => void
  /** Disabled state */
  disabled?: boolean
  /** Show seconds in the input */
  showSeconds?: boolean
  /** Additional className */
  className?: string
}

const IntuiTimePicker = React.forwardRef<HTMLInputElement, IntuiTimePickerProps>(
  ({ value, onChange, disabled = false, showSeconds = true, className }, ref) => {
    return (
      <input
        ref={ref}
        type="time"
        step={showSeconds ? 1 : 60}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        className={cn(
          'inline-flex h-8 w-[150px] items-center rounded-lg border border-input',
          'bg-background px-2.5 py-1 text-sm text-foreground',
          'focus-visible:outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/50',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      />
    )
  },
)
IntuiTimePicker.displayName = 'IntuiTimePicker'

/* ─── IntuiDateTimePicker ─── */

export interface IntuiDateTimePickerProps {
  /** Currently selected date */
  date?: Date
  /** Current time value */
  time?: string
  /** Callback when date changes */
  onDateChange?: (date: Date | undefined) => void
  /** Callback when time changes */
  onTimeChange?: (time: string) => void
  /** Date picker placeholder */
  datePlaceholder?: string
  /** Date picker trigger type */
  type?: DatePickerType
  /** Show seconds in time picker */
  showSeconds?: boolean
  /** Disabled state */
  disabled?: boolean
  /** Additional className for the wrapper */
  className?: string
}

function IntuiDateTimePicker({
  date,
  time,
  onDateChange,
  onTimeChange,
  datePlaceholder,
  type = 'icon-right',
  showSeconds = true,
  disabled = false,
  className,
}: IntuiDateTimePickerProps) {
  const { t } = useTranslation()
  const resolvedPlaceholder = datePlaceholder ?? t('common:date_picker.select_date')

  return (
    <div className={cn('flex items-end gap-2', className)}>
      <IntuiDatePicker
        value={date}
        onChange={onDateChange}
        placeholder={resolvedPlaceholder}
        type={type}
        disabled={disabled}
      />
      <IntuiTimePicker
        value={time}
        onChange={onTimeChange}
        showSeconds={showSeconds}
        disabled={disabled}
      />
    </div>
  )
}
IntuiDateTimePicker.displayName = 'IntuiDateTimePicker'

export {
  IntuiDatePicker,
  IntuiDateRangePicker,
  IntuiTimePicker,
  IntuiDateTimePicker,
}
